/**
 * KML export (M6.md acceptance criterion 11). Serializes the current saved map
 * state (points + the active title/fields) to KML for Google Earth / Maps
 * import. Pure and dependency-free — the same PointSet the renderer uses in;
 * a valid KML document out. PNG export is a client-side canvas capture and PDF
 * is deferred; KML is the pure serializer that belongs in the engine.
 */

import type { Point, PointSet } from "@/lib/map/pointset";

function xmlEscape(s: string): string {
  return s.replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" })[c]!);
}

function placemark(point: Point, titleColumn: string | undefined, fields: string[]): string {
  const name = xmlEscape((titleColumn && point.props[titleColumn]) || point.id);
  const desc = fields
    .map((f) => `${f}: ${point.props[f] ?? ""}`)
    .join("\n");
  return [
    "    <Placemark>",
    `      <name>${name}</name>`,
    desc ? `      <description>${xmlEscape(desc)}</description>` : "",
    "      <Point>",
    `        <coordinates>${point.lng},${point.lat},0</coordinates>`,
    "      </Point>",
    "    </Placemark>",
  ]
    .filter(Boolean)
    .join("\n");
}

export interface KmlOptions {
  documentName?: string;
  titleColumn?: string;
  /** Property columns to include in each placemark description. */
  fields?: string[];
}

/** Serialize a PointSet to a KML document string. */
export function toKml(pointSet: PointSet, opts: KmlOptions = {}): string {
  const name = xmlEscape(opts.documentName ?? "Map export");
  const fields = opts.fields ?? [];
  const placemarks = pointSet.points.map((p) => placemark(p, opts.titleColumn, fields)).join("\n");
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    "  <Document>",
    `    <name>${name}</name>`,
    placemarks,
    "  </Document>",
    "</kml>",
  ].join("\n");
}
